import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  afterNextRender,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize, map } from 'rxjs';
import { TipoInstituto } from '../../../../core/enum/parametros/tipo-instituto.enum';
import { InstitutoPlano, InstitutosPlano } from '../../../../core/models/parametros/plano-institutos.model';
import { PlanoInstitutosService } from '../../../../core/services/parametros/plano-institutos.service';
import { ToastService } from '../../../../core/services/toast.service';
import { formatDateIsoToBr } from '../../../../core/utils/date-format.util';
import { VivestCheckboxValueAccessorDirective } from '../../../../shared/directives/checkbox-vivest/vivest-checkbox-value-accessor.directive';
import { VivestModalAreaVisivelDirective } from '../../../../shared/directives/modal-vivest/vivest-modal-area-visivel.directive';
import { VivestSwitchValueAccessorDirective } from '../../../../shared/directives/switch-vivest/vivest-switch-value-accessor.directive';
import { LoaderService } from '../../../../shared/services/loader/loader.service';
import { VivestInputValueAccessorDirective } from '../../../../shared/directives/input-vivest/vivest-input-value-accessor.directive';
import { EmptyStateComponent } from '../../../../shared/components/empty-state/empty-state.component';
import { MenuAcoesComponent, OpcaoMenuAcoes } from '../../../../shared/components/menu-acoes/menu-acoes.component';

type StatusInstituto = InstitutoPlano['status'];

/** Linha da tabela: o instituto (fixo) e os campos editáveis. */
interface LinhaInstituto {
  id: number;
  tipo: TipoInstituto | null;
  nome: string;
  /** Vigência da configuração do instituto (só leitura na aba), ISO `yyyy-MM-dd`. */
  vigenciaInicio: string;
  vigenciaFim: string;
  form: FormGroup;
}

/** Maior prazo de opção aceito, em dias. */
const PRAZO_MAXIMO_DIAS = 999;

/** Tela de seleção de instituto ("Procure por instituto"), aberta por "Associar Instituto". */
const ROTA_SELECAO_INSTITUTO = '/resgate';

/** Institutos com tela de configuração própria ("Configurar" no menu ⋮). */
const ROTAS_CONFIGURACAO: Partial<Record<TipoInstituto, string>> = {
  [TipoInstituto.Resgate]: '/resgate',
};

/**
 * Aba "Instituto" da parametrização do plano: institutos associados com status, vigência e
 * disponibilidade para o participante, e a permissão de combinar institutos. As mudanças só são
 * gravadas em "Salvar" (com confirmação); "Descartar alterações" volta ao que foi carregado.
 */
@Component({
  selector: 'app-plano-institutos',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    VivestSwitchValueAccessorDirective,
    VivestCheckboxValueAccessorDirective,
    VivestModalAreaVisivelDirective,
    MenuAcoesComponent,
    EmptyStateComponent,
    VivestInputValueAccessorDirective,
  ],
  // Web components do design system Vivest (button, icon, icon-button, tag, switch, checkbox, card, modal).
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './plano-institutos.component.html',
  styleUrl: './plano-institutos.component.scss',
})
export class PlanoInstitutosComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly institutosService = inject(PlanoInstitutosService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  /** Id da parametrização do plano. */
  readonly planoId = input.required<number>();

  readonly linhas = signal<LinhaInstituto[]>([]);
  readonly carregando = signal(true);
  readonly salvando = signal(false);
  readonly confirmandoSalvar = signal(false);

  readonly form = this.fb.nonNullable.group({
    institutos: this.fb.array<FormGroup>([]),
    /** Dias após o recebimento do extrato de desligamento (inteiro de 1 a 999). */
    prazoDias: ['', [Validators.required, Validators.pattern(/^\d+$/), Validators.min(1), Validators.max(PRAZO_MAXIMO_DIAS)]],
    permitirCombinacao: [false],
  });

  /** Último estado carregado ou salvo, para o "Descartar alterações". */
  private carregado: InstitutosPlano | null = null;

  /**
   * Muda a cada alteração do formulário (um objeto novo por emissão), para o `podeSalvar` ser
   * recalculado mesmo quando o status continua "VALID".
   */
  private readonly revisao = toSignal(this.form.valueChanges.pipe(map(() => ({}))), { initialValue: {} });

  /** Há mudanças ainda não salvas (habilita o "Descartar alterações"). */
  readonly temAlteracoes = computed(() => {
    this.revisao();
    return this.form.dirty && !this.salvando();
  });

  /** "Salvar" só habilita com alguma alteração e o prazo válido. */
  readonly podeSalvar = computed(() => {
    this.revisao();
    return this.form.dirty && this.form.valid && !this.salvando();
  });

  get institutos(): FormArray<FormGroup> {
    return this.form.controls.institutos;
  }

  ngOnInit(): void {
    // Prazo em dias: só dígitos ficam no campo.
    this.form.controls.prazoDias.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((valor) => {
      const digitos = valor.replace(/\D/g, '');
      if (digitos !== valor) {
        this.form.controls.prazoDias.setValue(digitos);
      }
    });

    this.loaderService.showLoader();
    this.institutosService
      .obterInstitutos(this.planoId())
      .pipe(
        finalize(() => this.carregando.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (dados) => {
          this.aplicar(dados);
          afterNextRender(() => this.loaderService.hideLoader(), { injector: this.injector });
        },
        error: () => {
          this.loaderService.hideLoader();
          this.toastService.show('Não foi possível carregar os institutos do plano.', 'error');
        },
      });
  }

  // ── linha ───────────────────────────────────────────────────────────────────

  status(linha: LinhaInstituto): StatusInstituto {
    return linha.form.controls['status'].value;
  }


  /**
   * Inativar desliga e bloqueia o "Disponível para Participante"; ativar libera o switch. A mudança
   * vale na tela e é gravada pelo "Salvar".
   */
  alternarStatus(linha: LinhaInstituto): void {
    const status = linha.form.controls['status'];
    status.setValue(status.value === 'Ativo' ? 'Inativo' : 'Ativo', { emitEvent: false });
    this.sincronizarDisponivel(linha.form);
    // Marca o próprio campo (não só o formulário): habilitar/desabilitar o switch recalcula o
    // "dirty" do formulário a partir dos campos, e a marcação só no formulário se perderia.
    status.markAsDirty();
    status.updateValueAndValidity();
  }

  /** Opções do menu ⋮ da linha: configurar o instituto e ativá-lo ou inativá-lo. */
  opcoesDe(linha: LinhaInstituto): readonly OpcaoMenuAcoes[] {
    const ativo = this.status(linha) === 'Ativo';
    const configuravel = !!this.rotaConfiguracao(linha);
    return [
      {
        id: 'configurar',
        rotulo: 'Configurar',
        icone: 'edit-2',
        desabilitada: !configuravel,
        dica: configuravel ? undefined : 'Configuração disponível em breve',
      },
      {
        id: 'status',
        rotulo: ativo ? 'Inativar' : 'Ativar',
        icone: ativo ? 'slash' : 'check-circle',
        separada: true,
        // Sem tela de configuração, o instituto fica inativo: só dá para inativar, não ativar.
        desabilitada: !ativo && !configuravel,
        dica: !ativo && !configuravel ? 'Configuração disponível em breve' : undefined,
      },
    ];
  }

  escolherAcao(linha: LinhaInstituto, acao: string): void {
    if (acao === 'configurar') {
      this.configurar(linha);
    } else if (acao === 'status') {
      this.alternarStatus(linha);
    }
  }

  /**
   * "Associar Instituto": abre a seleção de instituto ("Procure por instituto") do plano, onde o
   * instituto escolhido é cadastrado ou, se já tiver configuração no plano, editado.
   */
  associar(): void {
    void this.router.navigate([ROTA_SELECAO_INSTITUTO], { queryParams: { plano: this.planoId() } });
  }

  /** Abre a elegibilidade do extrato de desligamento do plano. */
  abrirExtratoDesligamento(): void {
    void this.router.navigate(['/planos', this.planoId(), 'extrato-desligamento']);
  }

  /** Abre a configuração do instituto já selecionado (hoje só o Resgate tem tela). */
  private configurar(linha: LinhaInstituto): void {
    const rota = this.rotaConfiguracao(linha);
    if (rota) {
      void this.router.navigate([rota], { queryParams: { plano: this.planoId(), instituto: linha.tipo } });
    }
  }

  private rotaConfiguracao(linha: LinhaInstituto): string | undefined {
    return linha.tipo ? ROTAS_CONFIGURACAO[linha.tipo] : undefined;
  }

  /**
   * Mensagem de erro do prazo, depois que o campo é tocado ou que qualquer outra coisa da aba muda
   * (sem prazo válido o "Salvar" não habilita, e a mensagem diz o porquê).
   */
  erroPrazo(): string {
    const prazo = this.form.controls.prazoDias;
    if (prazo.valid || !(prazo.touched || this.form.dirty)) {
      return '';
    }
    return prazo.hasError('required') ? 'Informe o prazo em dias.' : `Use um número de 1 a ${PRAZO_MAXIMO_DIAS}.`;
  }

  /** Data da vigência em dd/mm/aaaa; vazia é "Não informado" (início) ou "Sem término" (fim). */
  data(iso: string, vazio: string): string {
    return iso ? formatDateIsoToBr(iso) : vazio;
  }

  // ── salvar / cancelar ───────────────────────────────────────────────────────

  salvar(): void {
    this.form.markAllAsTouched();
    if (this.podeSalvar()) {
      this.confirmandoSalvar.set(true);
    }
  }

  cancelarSalvar(): void {
    this.confirmandoSalvar.set(false);
  }

  confirmarSalvar(): void {
    this.confirmandoSalvar.set(false);
    if (this.form.invalid || this.salvando()) {
      return;
    }
    const dados = this.valores();
    this.salvando.set(true);
    this.loaderService.showLoader();
    this.institutosService
      .salvarInstitutos(this.planoId(), dados)
      .pipe(
        finalize(() => {
          this.salvando.set(false);
          this.loaderService.hideLoader();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (salvos) => {
          this.aplicar(salvos);
          this.toastService.show('Institutos do plano salvos.', 'success');
        },
        error: () => this.toastService.show('Não foi possível salvar os institutos do plano.', 'error'),
      });
  }

  /** "Descartar alterações": volta ao último estado carregado ou salvo, sem sair da aba. */
  descartar(): void {
    if (this.carregado) {
      this.aplicar(this.carregado);
    }
  }

  // ── formulário ──────────────────────────────────────────────────────────────

  private aplicar(dados: InstitutosPlano): void {
    this.carregado = structuredClone(dados);
    const atuais = this.linhas();
    const mesmasLinhas =
      atuais.length === dados.institutos.length &&
      atuais.every((linha, i) => linha.id === dados.institutos[i].id);

    // Mesmos institutos (descartar, ou salvar sem mudar a lista): restaura os valores nos controles
    // que já estão ligados à tela. Trocar os controles deixaria a tabela presa aos antigos, porque a
    // linha (mesmo instituto) não é recriada.
    const linhas = dados.institutos.map((instituto, i) => ({
      id: instituto.id,
      tipo: instituto.tipo,
      nome: instituto.nome,
      vigenciaInicio: instituto.vigenciaInicio,
      vigenciaFim: instituto.vigenciaFim,
      form: mesmasLinhas ? this.restaurarLinha(atuais[i].form, instituto) : this.criarLinha(instituto),
    }));
    if (!mesmasLinhas) {
      this.institutos.clear({ emitEvent: false });
      linhas.forEach((linha) => this.institutos.push(linha.form, { emitEvent: false }));
    }
    // Plano novo: prazo em branco até ser informado.
    this.form.controls.prazoDias.setValue(dados.prazoOpcaoDias === null ? '' : String(dados.prazoOpcaoDias), {
      emitEvent: false,
    });
    this.form.controls.permitirCombinacao.setValue(dados.permitirCombinacao, { emitEvent: false });
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.form.updateValueAndValidity();
    this.linhas.set(linhas);
  }

  /** Na aba, só o status e a disponibilidade mudam; a vigência é editada na configuração do instituto. */
  private criarLinha(instituto: InstitutoPlano): FormGroup {
    const grupo = this.fb.nonNullable.group({
      status: [instituto.status],
      disponivel: [instituto.disponivelParticipante],
    });
    this.sincronizarDisponivel(grupo);
    return grupo;
  }

  private restaurarLinha(grupo: FormGroup, instituto: InstitutoPlano): FormGroup {
    grupo.controls['disponivel'].enable({ emitEvent: false });
    grupo.reset({ status: instituto.status, disponivel: instituto.disponivelParticipante }, { emitEvent: false });
    this.sincronizarDisponivel(grupo);
    return grupo;
  }

  /** Instituto inativo não fica disponível para o participante: o switch vem desligado e bloqueado. */
  private sincronizarDisponivel(grupo: FormGroup): void {
    const disponivel = grupo.controls['disponivel'];
    if (grupo.controls['status'].value === 'Ativo') {
      disponivel.enable({ emitEvent: false });
    } else {
      disponivel.setValue(false, { emitEvent: false });
      disponivel.disable({ emitEvent: false });
    }
  }

  private valores(): InstitutosPlano {
    return {
      prazoOpcaoDias: Number(this.form.controls.prazoDias.value),
      permitirCombinacao: this.form.controls.permitirCombinacao.value,
      institutos: this.linhas().map(({ id, tipo, nome, vigenciaInicio, vigenciaFim, form }) => {
        const valor = form.getRawValue();
        return {
          id,
          tipo,
          nome,
          status: valor.status,
          vigenciaInicio,
          vigenciaFim,
          disponivelParticipante: valor.status === 'Ativo' && valor.disponivel,
        };
      }),
    };
  }
}
